import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { GatewayData } from "pages/Breakers/typings";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/store/store";
import { selectGateway } from "redux/slices/gatewaySlice";
import { getGatewayListByUserId } from "api/common/OnAppLoad/getDataOnLoad";
import { getUserId } from "utils/Common";
import { signalRService } from "services/signalRService";

const useHomepage = () => {
  const dispatch = useDispatch();

  const selectedGateway = useSelector(
    (state: RootState) => state.gateway.selectedGateway,
  );

  const selectedGatewayRef = useRef(selectedGateway);
  selectedGatewayRef.current = selectedGateway;

  const userId = getUserId(sessionStorage.getItem("userId"));

  const {
    data: gatewayListData,
    isFetching: isGatewayListFetching,
    isError,
    error,
    refetch: refetchGatewayList,
  } = useQuery<GatewayData[], Error>({
    queryKey: ["gatewayList", userId],
    queryFn: () => getGatewayListByUserId({ userId }),

    refetchOnMount: true,

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    staleTime: 1000,
  });

  useEffect(() => {
    const current = selectedGatewayRef.current;
    if (gatewayListData && current) {
      const freshGateway = gatewayListData.find(
        (gw: GatewayData) => gw.id === current.id,
      );
      if (
        freshGateway &&
        (freshGateway.label !== current.label ||
          freshGateway.isOnline !== current.isOnline)
      ) {
        dispatch(selectGateway(freshGateway));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayListData, dispatch]);

  const isPersistRehydrated =
    (gatewayListData !== undefined &&
      (gatewayListData.length === 0 || !!selectedGateway)) ||
    isError;

  const isLoading = isGatewayListFetching || !isPersistRehydrated;

  useEffect(() => {
    let isMounted = true;

    const handleGatewayUpdateFeed = () => {
      if (isMounted) {
        refetchGatewayList();
      }
    };

    const setupSignalR = async () => {
      try {
        await signalRService.startConnection();

        const connId = await signalRService.invoke<string>("GetConnectionId");
        if (!connId || !isMounted) return;

        await signalRService.invoke("Register", userId, `${userId}`, connId, 1);
        await signalRService.invoke("GetRegistration", userId, connId);

        signalRService.on("GatewayUpdateFeedReceived", handleGatewayUpdateFeed);
      } catch (err) {
        if (isMounted) {
          console.error("[SignalR] Home — Failed to set up connection:", err);
        }
      }
    };

    setupSignalR();

    return () => {
      isMounted = false;

      signalRService.off("GatewayUpdateFeedReceived", handleGatewayUpdateFeed);
    };
  }, [userId, refetchGatewayList]);

  return {
    gatewayListData,
    selectedGateway,
    isLoading,
    isError,
    error,
  };
};

export default useHomepage;
